import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown, ListChecks, CalendarClock, CheckCircle2 } from "lucide-react";
import { STAGE_STATUS_META } from "@/lib/format";
import { formatDate } from "@/lib/format";

const STATUS_OPTIONS = [
  { value: "pendente", label: "Pendente" },
  { value: "em_andamento", label: "Em andamento" },
  { value: "concluido", label: "Concluído" },
];

const emptyForm = { name: "", description: "", status: "pendente", due_date: "" };

export function ProjectStagesTab({ projectId, isAdmin, onProgressChange }) {
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/projects/${projectId}/stages`);
      setStages(r.data);
      const done = r.data.filter((s) => s.status === "concluido").length;
      onProgressChange?.(r.data.length ? Math.round((done / r.data.length) * 100) : 0);
    } catch (e) { toast.error("Erro ao carregar cronograma"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [projectId]);

  const openNew = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({ name: s.name, description: s.description || "", status: s.status, due_date: s.due_date ? s.due_date.slice(0, 10) : "" });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const payload = { ...form, due_date: form.due_date || null };
      if (editing) {
        await api.patch(`/stages/${editing.id}`, payload);
        toast.success("Etapa atualizada");
      } else {
        await api.post(`/projects/${projectId}/stages`, payload);
        toast.success("Etapa criada");
      }
      setOpen(false);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao salvar etapa"); }
    finally { setSaving(false); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Excluir a etapa "${s.name}"?`)) return;
    try { await api.delete(`/stages/${s.id}`); toast.success("Etapa removida"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro ao excluir"); }
  };

  const toggleDone = async (s) => {
    try {
      await api.patch(`/stages/${s.id}`, { status: s.status === "concluido" ? "pendente" : "concluido" });
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const move = async (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= stages.length) return;
    const reordered = [...stages];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    setStages(reordered);
    try {
      await api.post(`/projects/${projectId}/stages/reorder`, { stage_ids: reordered.map((s) => s.id) });
    } catch (e) { toast.error("Erro ao reordenar"); await load(); }
  };

  const done = stages.filter((s) => s.status === "concluido").length;
  const percent = stages.length ? Math.round((done / stages.length) * 100) : 0;

  if (loading) return <div className="space-y-3">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-16 rounded-xl" />)}</div>;

  return (
    <div className="space-y-6" data-testid="cronograma-tab">
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <ListChecks size={16} className="text-primary" />
              <h3 className="font-heading font-semibold">Progresso do cronograma</h3>
            </div>
            <span className="font-heading text-xl font-bold tabular-nums" data-testid="cronograma-progress-percent">{percent}%</span>
          </div>
          <Progress value={percent} className="h-2" />
          <p className="mt-2 text-xs text-muted-foreground">{done} de {stages.length} etapas concluídas</p>
        </CardContent>
      </Card>

      {isAdmin && (
        <div className="flex justify-end">
          <Button onClick={openNew} className="gap-2" data-testid="new-stage-btn">
            <Plus size={16} /> Nova etapa
          </Button>
        </div>
      )}

      {stages.length === 0 ? (
        <Card><CardContent className="p-10 text-sm text-muted-foreground text-center">Nenhuma etapa cadastrada ainda.</CardContent></Card>
      ) : (
        <ul className="space-y-3">
          {stages.map((s, idx) => {
            const meta = STAGE_STATUS_META[s.status] || STAGE_STATUS_META.pendente;
            return (
              <li key={s.id} className="bg-card border rounded-xl p-4 flex items-start gap-3" data-testid={`stage-item-${s.id}`}>
                <button
                  onClick={() => isAdmin && toggleDone(s)}
                  disabled={!isAdmin}
                  data-testid={`stage-toggle-${s.id}`}
                  className={`mt-0.5 shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${s.status === "concluido" ? "bg-emerald-500 border-emerald-500" : "border-muted-foreground/40"} ${isAdmin ? "cursor-pointer" : "cursor-default"}`}
                >
                  {s.status === "concluido" && <CheckCircle2 size={14} className="text-white" />}
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium">{s.name}</p>
                    <span className="text-[11px] font-medium rounded-full px-2 py-0.5 border" style={{ color: meta.color, borderColor: `${meta.color}40`, backgroundColor: meta.bg }} data-testid={`stage-status-${s.id}`}>{meta.label}</span>
                  </div>
                  {s.description && <p className="text-sm text-muted-foreground mt-1">{s.description}</p>}
                  <div className="mt-2 flex items-center gap-4 text-xs text-muted-foreground">
                    {s.due_date && <span className="inline-flex items-center gap-1"><CalendarClock size={12} /> Previsto: {formatDate(s.due_date)}</span>}
                    {s.completed_date && <span className="inline-flex items-center gap-1 text-emerald-600"><CheckCircle2 size={12} /> Concluído: {formatDate(s.completed_date)}</span>}
                  </div>
                </div>
                {isAdmin && (
                  <div className="flex flex-col sm:flex-row items-center gap-1 shrink-0">
                    <Button variant="ghost" size="sm" onClick={() => move(idx, -1)} disabled={idx === 0} data-testid={`stage-up-${s.id}`}><ArrowUp size={14} /></Button>
                    <Button variant="ghost" size="sm" onClick={() => move(idx, 1)} disabled={idx === stages.length - 1} data-testid={`stage-down-${s.id}`}><ArrowDown size={14} /></Button>
                    <Button variant="ghost" size="sm" onClick={() => openEdit(s)} data-testid={`stage-edit-${s.id}`}><Pencil size={14} /></Button>
                    <Button variant="ghost" size="sm" onClick={() => remove(s)} className="text-brand-red hover:text-brand-red" data-testid={`stage-delete-${s.id}`}><Trash2 size={14} /></Button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Editar etapa" : "Nova etapa"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs">Nome *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="stage-form-name" placeholder="Ex: Fundação concluída" />
            </div>
            <div>
              <Label className="text-xs">Descrição (opcional)</Label>
              <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="stage-form-description" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Status</Label>
                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
                  className="h-10 w-full rounded-md border bg-background px-3 text-sm mt-1" data-testid="stage-form-status">
                  {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs">Data prevista</Label>
                <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="stage-form-due-date" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={!form.name.trim() || saving} data-testid="stage-form-save">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
